import { useEffect, useState } from 'react';
import { Colorful, ColorResult } from '@uiw/react-color';
import { hexToHsva, hsvaToHexa, HsvaColor } from '@uiw/color-convert';
import styled from 'styled-components';
import Card from './ui/Card';
import { ui } from './ui/tokens';
import { PALETTE_GROUPS } from './palettes';
import ComplementaryWheel from '../assets/design/wheel-complementary.svg';
import TriadicWheel from '../assets/design/wheel-triadic.svg';

export type PencilColourCardProps = {
	open: boolean;
	title?: string;
	/** Current colour as #RRGGBBAA or #RRGGBB */
	color: string;
	onChange: (hexa: string) => void;
	onClose: () => void;
	/** Recently used colours; hidden when empty or omitted */
	recentColors?: string[];
	onRecentSelect?: (hex: string) => void;
	/** Palettes are hidden for the background colour, which is usually dark */
	showPalettes?: boolean;
};

const sameColour = (a: string, b: string) => a.slice(0, 7).toUpperCase() === b.slice(0, 7).toUpperCase();

/**
 * "Specify pencil colour" card (style guide page 3 / R18): recent colours,
 * a free colour picker and tints & shades palettes.
 */
function PencilColourCard({ open, title = 'Specify pencil colour', color, onChange, onClose, recentColors, onRecentSelect, showPalettes = true }: PencilColourCardProps) {
	// Keep the picker's own HSVA so hue survives greys (where hex has no hue);
	// only re-sync when the colour is changed from outside the picker.
	const [hsva, setHsva] = useState<HsvaColor>(() => hexToHsva(color || '#000000ff'));
	useEffect(() => {
		if (color && !sameColour(hsvaToHexa(hsva), color)) {
			setHsva(hexToHsva(color));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [color]);

	const handlePicker = (c: ColorResult) => {
		setHsva(c.hsva);
		onChange(c.hexa);
	};

	return (
		<Card open={open} title={title} onClose={onClose}>
			{!!recentColors?.length && (
				<>
					<Heading>Recent colours</Heading>
					<SwatchRow>
						{recentColors.map((c, i) => (
							<Swatch key={`${c}-${i}`} type="button" $color={c} $selected={sameColour(c, color)} aria-label={`Recent colour ${c}`} onClick={() => (onRecentSelect ? onRecentSelect(c) : onChange(c))} />
						))}
					</SwatchRow>
				</>
			)}

			<Heading>Select a colour</Heading>
			<PickerWrapper>
				<Colorful color={hsva} onChange={handlePicker} style={{ width: '100%' }} />
			</PickerWrapper>

			{showPalettes && (
				<>
					<Heading>Colour palettes</Heading>
					<ColumnLabels>
						<SmallLabel>Tints</SmallLabel>
						<span />
						<SmallLabel>Shades</SmallLabel>
					</ColumnLabels>
					{PALETTE_GROUPS.map(group => (
						<div key={group.label}>
							<GroupLabel>{group.label}</GroupLabel>
							{group.rows.map(row => (
								<PaletteRow key={row.base}>
									<Swatches>
										{row.tints.map(c => (
											<Swatch key={c} type="button" $color={c} $selected={sameColour(c, color)} aria-label={c} onClick={() => onChange(c)} />
										))}
									</Swatches>
									<Swatch type="button" $color={row.base} $selected={sameColour(row.base, color)} aria-label={row.base} onClick={() => onChange(row.base)} />
									<Swatches>
										{row.shades.map(c => (
											<Swatch key={c} type="button" $color={c} $selected={sameColour(c, color)} aria-label={c} onClick={() => onChange(c)} />
										))}
									</Swatches>
								</PaletteRow>
							))}
						</div>
					))}

					{/* Educational aid from the style guide (R18): which colours sit
					    opposite each other, and which three form a triad */}
					<Wheels>
						<Wheel>
							<img src={ComplementaryWheel} alt="Colour wheel showing a pair of complementary colours" />
							<SmallLabel>Complementary colours</SmallLabel>
						</Wheel>
						<Wheel>
							<img src={TriadicWheel} alt="Colour wheel showing three triadic colours" />
							<SmallLabel>Triadic colours</SmallLabel>
						</Wheel>
					</Wheels>
				</>
			)}
		</Card>
	);
}

export default PencilColourCard;

const Heading = styled.h3`
	margin: 20px 0 10px;
	font-family: ${ui.font};
	font-size: 16px;
	font-weight: 600;
	color: ${ui.text};
`;

const SwatchRow = styled.div`
	display: flex;
	gap: 8px;
	overflow-x: auto;
	padding-bottom: 2px;
	scrollbar-width: none;
	&::-webkit-scrollbar {
		display: none;
	}
`;

const Swatch = styled.button<{ $color: string; $selected: boolean }>`
	flex-shrink: 0;
	width: 26px;
	height: 26px;
	padding: 0;
	border-radius: 4px;
	background-color: ${props => props.$color};
	border: 2px solid ${props => (props.$selected ? ui.text : props.$color.toUpperCase().startsWith('#333333') ? ui.label : 'transparent')};
	box-shadow: ${props => (props.$selected ? `0 0 0 2px ${ui.gold}` : 'none')};
	cursor: pointer;
`;

const PickerWrapper = styled.div`
	/* room for the picker handles, which overhang the saturation box */
	padding: 12px 11px 0;

	.w-color-colorful {
		width: 100% !important;
	}
	.w-color-saturation {
		height: 64px !important;
		border-radius: 6px !important;
		margin-bottom: 14px !important;
		border-bottom: none !important;
	}
	.w-color-hue,
	.w-color-alpha {
		height: 12px !important;
		border-radius: 6px !important;
		margin-bottom: 14px !important;
	}
	.w-color-interactive > div > div {
		width: 22px !important;
		height: 22px !important;
		border: 2px solid #fff !important;
	}
`;

const ColumnLabels = styled.div`
	display: grid;
	grid-template-columns: 1fr 26px 1fr;
	gap: 12px;
	text-align: center;
	margin-bottom: 4px;
`;

const GroupLabel = styled.div`
	margin: 8px 0 6px;
	text-align: center;
	font-family: ${ui.font};
	font-size: 9px;
	font-weight: 700;
	text-transform: uppercase;
	color: ${ui.label};
`;

const SmallLabel = styled.span`
	font-family: ${ui.font};
	font-size: 9px;
	font-weight: 700;
	text-transform: uppercase;
	color: ${ui.label};
`;

const PaletteRow = styled.div`
	display: grid;
	grid-template-columns: 1fr 26px 1fr;
	gap: 12px;
	align-items: center;
	margin-bottom: 8px;
`;

const Swatches = styled.div`
	display: flex;
	justify-content: space-between;
`;

const Wheels = styled.div`
	display: flex;
	justify-content: space-around;
	margin-top: 22px;
`;

const Wheel = styled.figure`
	margin: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;

	img {
		width: 110px;
		height: 110px;
	}
`;
